import React, { useEffect, useState, useMemo } from "react";
import {
  Avatar,
  Input,
  Tag,
  Spin,
  Empty,
  Badge,
  Tooltip,
  Button,
  Grid,
} from "antd";

const { useBreakpoint } = Grid;
import {
  SearchOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  LogoutOutlined,
  LoginOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getTodayCheckinsApi } from "../../apiservice/hrpayroll";

// ─── tiny helpers ────────────────────────────────────────────────────────────

const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const avatarColor = (name = "") => {
  const colors = [
    "#4f6ef7", "#e8614a", "#27ae7a", "#e09c35",
    "#9b59b6", "#16a085", "#d35400", "#2980b9",
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
};

// ─── sub-components ──────────────────────────────────────────────────────────

function StatPill({ icon, label, count, color }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: `${color}14`,
        border: `1px solid ${color}30`,
        borderRadius: 10,
        padding: "8px 16px",
        flex: "1 1 130px",
        minWidth: 130,
      }}
    >
      <span style={{ color, fontSize: 20 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1.1 }}>
          {count}
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>{label}</div>
      </div>
    </div>
  );
}

function EmployeeRow({ emp, type }) {
  const isIn = type === "in";
  const isLate = type === "late";
  const isAbsent = type === "absent";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderRadius: 10,
        background: "#fafafa",
        border: "1px solid #f0f0f0",
        marginBottom: 8,
        transition: "box-shadow 0.2s",
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.08)")
      }
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      {/* Avatar */}
      <Avatar
        src={emp.employee_photo_path || null}
        size={38}
        style={{
          background: !emp.employee_photo_path
            ? avatarColor(emp.employee_name || emp.name || "")
            : undefined,
          flexShrink: 0,
          fontWeight: 600,
          opacity: isAbsent ? 0.6 : 1,
        }}
      >
        {!emp.employee_photo_path &&
          initials(emp.employee_name || emp.name || "")}
      </Avatar>

      {/* Name + times */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: isAbsent ? "#999" : "#1a1a2e",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {emp.employee_name || emp.name}
        </div>

        {!isLate && !isAbsent && (
          <div
            style={{
              fontSize: 11,
              color: "#999",
              marginTop: 2,
              display: "flex",
              gap: 10,
            }}
          >
            {emp.check_in && (
              <span>
                <LoginOutlined style={{ marginRight: 3 }} />
                {emp.check_in.slice(0, 5)}
              </span>
            )}
            {emp.check_out && (
              <span>
                <LogoutOutlined style={{ marginRight: 3 }} />
                {emp.check_out.slice(0, 5)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tag */}
      {isIn && (
        <Tag
          color="success"
          style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}
        >
          Still In
        </Tag>
      )}
      {type === "out" && (
        <Tag
          color="processing"
          style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}
        >
          Checked Out
        </Tag>
      )}
      {isLate && (
        <Tag
          color="error"
          style={{ borderRadius: 20, fontWeight: 600, fontSize: 11 }}
        >
          {emp.minutes} min late
        </Tag>
      )}
      {isAbsent && (
        <Tag
          color="default"
          style={{
            borderRadius: 20,
            fontWeight: 600,
            fontSize: 11,
            color: "#999",
          }}
        >
          Not Checked In
        </Tag>
      )}
    </div>
  );
}

function Section({ icon, title, color, count, children, emptyText }) {
  return (
    <div style={{ marginBottom: 28 }}>
      {/* Section header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          paddingBottom: 8,
          borderBottom: `2px solid ${color}22`,
        }}
      >
        <span style={{ color, fontSize: 16 }}>{icon}</span>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#1a1a2e" }}>
          {title}
        </span>
        <Badge
          count={count}
          style={{
            background: color,
            boxShadow: "none",
            fontSize: 10,
          }}
        />
      </div>

      {count === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ fontSize: 12, color: "#bbb" }}>{emptyText}</span>
          }
          style={{ padding: "16px 0" }}
        />
      ) : (
        <div style={{ maxHeight: 280, overflowY: "auto", paddingRight: 2 }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function ClockInOutTab() {
  const screens = useBreakpoint();
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getTodayCheckinsApi({});
      if (!res.error) {
        setCheckins(res.data?.data || []);
        setLastRefresh(dayjs().format("hh:mm A"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const { stillIn, checkedOut, lateArrivals, notCheckedIn } = useMemo(() => {
    const list = Array.isArray(checkins) ? checkins : [];
    const q = searchText.toLowerCase();

    const filter = (arr) =>
      q
        ? arr.filter(
            (e) =>
              (e.employee_name || e.name || "")
                .toLowerCase()
                .includes(q)
          )
        : arr;

    const rawStillIn = list.filter((e) => e.check_in && !e.check_out);
    const rawOut = list.filter((e) => e.check_in && e.check_out);
    const rawLate = list
      .filter((e) => e.late_minutes > 0)
      .map((e) => ({
        employee_id: e.employee_id,
        employee_name: e.employee_name,
        employee_photo_path: e.employee_photo_path,
        minutes: e.late_minutes,
      }));
    const rawNotCheckedIn = list.filter((e) => !e.check_in);

    return {
      stillIn: filter(rawStillIn),
      checkedOut: filter(rawOut),
      lateArrivals: filter(rawLate),
      notCheckedIn: filter(rawNotCheckedIn),
    };
  }, [checkins, searchText]);

  return (
    <div style={{ padding: "4px 0" }}>
      {/* ── Top bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>
            Today's Attendance
          </div>
          {lastRefresh && (
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
              Last updated {lastRefresh}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flex: screens.sm ? "0 0 auto" : "1 1 100%",
          }}
        >
          <Input
            prefix={<SearchOutlined style={{ color: "#ccc" }} />}
            placeholder="Search employee…"
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: screens.sm ? 200 : undefined, flex: screens.sm ? "0 0 auto" : 1, borderRadius: 8 }}
          />
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined spin={loading} />}
              onClick={load}
              style={{ borderRadius: 8 }}
            />
          </Tooltip>
        </div>
      </div>

      {/* ── Stat pills ── */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 24,
        }}
      >
        <StatPill
          icon={<ClockCircleOutlined />}
          label="Still Clocked In"
          count={stillIn.length}
          color="#27ae7a"
        />
        <StatPill
          icon={<CheckCircleOutlined />}
          label="Checked Out"
          count={checkedOut.length}
          color="#4f6ef7"
        />
        <StatPill
          icon={<WarningOutlined />}
          label="Late Arrivals"
          count={lateArrivals.length}
          color="#e09c35"
        />
        <StatPill
          icon={<CloseCircleOutlined />}
          label="Not Checked In"
          count={notCheckedIn.length}
          color="#e74c3c"
        />
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {/* ── Sections ── */}
      {!loading && (
        <>
          <Section
            icon={<LoginOutlined />}
            title="Still Clocked In"
            color="#27ae7a"
            count={stillIn.length}
            emptyText="No one is currently clocked in"
          >
            {stillIn.map((emp) => (
              <EmployeeRow key={emp.employee_id} emp={emp} type="in" />
            ))}
          </Section>

          <Section
            icon={<LogoutOutlined />}
            title="Checked Out"
            color="#4f6ef7"
            count={checkedOut.length}
            emptyText="No one has checked out yet"
          >
            {checkedOut.map((emp) => (
              <EmployeeRow key={emp.employee_id} emp={emp} type="out" />
            ))}
          </Section>

          <Section
            icon={<WarningOutlined />}
            title="Late Arrivals"
            color="#e09c35"
            count={lateArrivals.length}
            emptyText="No late arrivals today"
          >
            {lateArrivals.map((emp) => (
              <EmployeeRow key={emp.employee_id} emp={emp} type="late" />
            ))}
          </Section>

          <Section
            icon={<CloseCircleOutlined />}
            title="Not Checked In"
            color="#e74c3c"
            count={notCheckedIn.length}
            emptyText="Everyone has checked in today"
          >
            {notCheckedIn.map((emp) => (
              <EmployeeRow key={emp.employee_id} emp={emp} type="absent" />
            ))}
          </Section>
        </>
      )}
    </div>
  );
}